<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-3">
        <button
          class="px-2.5 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50 flex items-center gap-1.5"
          :disabled="loading"
          @click="fetchLogs"
        >
          <ArrowPathIcon
            class="w-3.5 h-3.5"
            :class="{ 'animate-spin': loading }"
          />
          Refresh
        </button>
        <select
          v-model="selectedOperation"
          class="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
        >
          <option value="">All Operations</option>
          <option v-for="op in uniqueOperations" :key="op" :value="op">
            {{ formatAuditOperation(op) }}
          </option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="text-center py-6 text-gray-500 text-sm">
      Loading logs...
    </div>

    <div v-else-if="error" class="text-center py-6 text-red-500 text-sm">
      Error: {{ error }}
    </div>

    <div
      v-else-if="filteredLogs.length === 0"
      class="text-center py-6 text-gray-500 text-sm"
    >
      No logs found
    </div>

    <template v-else>
      <div class="border border-gray-200 rounded-lg overflow-hidden">
        <table class="w-full text-xs">
          <thead class="bg-gray-50">
            <tr class="border-b border-gray-200">
              <th class="text-left px-3 py-2 font-medium text-gray-600">
                Time
              </th>
              <th class="text-left px-3 py-2 font-medium text-gray-600">
                Operation
              </th>
              <th class="text-left px-3 py-2 font-medium text-gray-600">
                User
              </th>
              <th class="text-left px-3 py-2 font-medium text-gray-600">
                Resource
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 bg-white">
            <tr
              v-for="log in filteredLogs"
              :key="log.id"
              class="hover:bg-gray-50"
            >
              <td class="px-3 py-2 text-gray-500 whitespace-nowrap">
                {{ format(log.createdAt, dateFormat + ' HH:mm') }}
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span
                  class="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                  :class="getOperationBadgeClass(log.operation)"
                >
                  {{ formatAuditOperation(log.operation) }}
                </span>
              </td>
              <td class="px-3 py-2 whitespace-nowrap">
                <span class="text-gray-700">{{ getUserName(log.userId) }}</span>
              </td>
              <td class="px-3 py-2">
                <div class="flex flex-wrap gap-1">
                  <RouterLink
                    v-if="log.operation.startsWith('PROJECT') && log.projectId"
                    :to="{
                      path: '/projects',
                      query: { projectId: log.projectId },
                    }"
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-purple-50 text-purple-700 hover:bg-purple-100 cursor-pointer"
                  >
                    Project #{{ log.projectId }}
                  </RouterLink>
                  <RouterLink
                    v-else-if="log.reviewId"
                    :to="`/reviews/${log.reviewId}`"
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 cursor-pointer"
                  >
                    Review #{{ log.reviewId }}
                  </RouterLink>
                  <span v-else class="text-gray-400"> — </span>
                </div>
                <div class="mt-1">
                  <RouterLink
                    v-if="log.reviewItemId"
                    :to="{
                      name: RouteName.REVIEW_ITEM,
                      params: {
                        reviewItemId: log.reviewItemId,
                        id: log.reviewId,
                      },
                    }"
                    class="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-orange-50 text-orange-700 hover:bg-orange-100"
                  >
                    Review item #{{ log.reviewItemId }}
                  </RouterLink>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div
        class="flex items-center justify-between px-3 py-3 bg-neutral-background-default border border-neutral-border-default rounded-lg"
      >
        <p class="text-sm font-medium text-neutral-text-title">
          Total Logs: {{ total }}
        </p>
        <Pagination
          v-model:page="currentPage"
          v-model:items-per-page="itemsPerPage"
          :total-items="total"
          items-label="Logs"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { EvidenceServiceApiKey } from '@app/injectionKeys'
import { injectStrict } from '@app/utils/injectStrict'
import { ArrowPathIcon } from '@heroicons/vue/24/outline'
import type { DisplayAuditLogDto } from '@evidence/sdk'
import { computed, onMounted, ref, watch } from 'vue'
import { RouterLink } from 'vue-router'
import useUsers from '@app/composables/use-users'
import { useSettings } from '@app/composables/use-settings'
import { format } from 'date-fns'
import Pagination from '@app/components/Global/Pagination/Pagination.vue'
import { formatAuditOperation } from '@app/utils/auditOperationLabels'
import { RouteName } from '@app/router'

const evidenceService = injectStrict(EvidenceServiceApiKey)
const { users, refresh: refreshUsers } = useUsers()
const { dateFormat } = useSettings()

const logs = ref<DisplayAuditLogDto[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const selectedOperation = ref('')
const currentPage = ref(1)
const itemsPerPage = ref(15)
const total = ref(0)

const uniqueOperations = computed(() => {
  const operations = new Set(logs.value.map((log) => log.operation))
  return Array.from(operations).sort()
})

const filteredLogs = computed(() => {
  if (!selectedOperation.value) {
    return logs.value
  }
  return logs.value.filter((log) => log.operation === selectedOperation.value)
})

watch(selectedOperation, () => {
  currentPage.value = 1
})

watch(currentPage, () => {
  fetchLogs()
})

async function fetchLogs() {
  loading.value = true
  error.value = null
  try {
    const { data } = await evidenceService.api.auditLogsControllerFind({
      page: currentPage.value,
      pageSize: itemsPerPage.value,
    })
    logs.value = data.auditLogs
    total.value = data.total
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to fetch logs'
  } finally {
    loading.value = false
  }
}

function getOperationBadgeClass(operation: string): string {
  const op = operation.toLowerCase()
  if (op.includes('create') || op.includes('add')) {
    return 'bg-green-100 text-green-800'
  }
  if (op.includes('delete') || op.includes('remove')) {
    return 'bg-red-100 text-red-800'
  }
  if (op.includes('update') || op.includes('edit') || op.includes('modify')) {
    return 'bg-yellow-100 text-yellow-800'
  }
  if (op.includes('view') || op.includes('read') || op.includes('get')) {
    return 'bg-blue-100 text-blue-800'
  }
  return 'bg-gray-100 text-gray-800'
}

function getUserName(userId: string): string {
  const user = users.value.find((u) => u.id === userId)
  if (user) {
    return `${user.firstName} ${user.lastName}`.trim() || user.email
  }
  return userId
}
refreshUsers()

onMounted(() => {
  fetchLogs()
})
</script>
